import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Paperclip attach for the chat composer. A picked file goes straight to storage (Active Storage
// direct upload), then POST <uploadsUrl> records it and queues its read, so it is usually read
// before the message is sent. The server's chip (composer_upload) carries the hidden upload_ids[]
// field the message form submits; it is re-fetched every 2s until the read ends (data-pending).
// A removed or never-sent file is deleted overnight (Chat::UnlinkedUploadsCleanupJob).
export default class extends Controller {
  static targets = ["input", "list"]
  // max: files per message (Chat::Uploads::PROMPT_UPLOADS) — the server refuses a message past it.
  static values = { directUploadUrl: String, uploadsUrl: String, max: { type: Number, default: 5 } }

  connect() {
    this.element.addEventListener("submit", this.hold)
  }

  disconnect() {
    this.element.removeEventListener("submit", this.hold)
  }

  // A file's upload_ids[] field only exists once it is stored, so a send mid-upload went without the
  // file, and the file landed unlinked. The send is held instead and goes by itself once every file
  // is stored. Runs before Turbo's document-level submit handler, so preventing it stops the send.
  hold = (event) => {
    if (!this.uploading) return
    event.preventDefault()
    event.stopImmediatePropagation()
    this.held = true
    this.sendButton(false)
  }

  // A held send goes once nothing is uploading; a failed upload drops it, so the user sees the
  // failure before anything is sent.
  settle(failed = false) {
    if (!this.held || (this.uploading && !failed)) return
    this.held = false
    this.sendButton(true)
    if (!failed) this.element.requestSubmit()
  }

  sendButton(enabled) {
    const button = this.element.querySelector('button[type="submit"]')
    if (!button) return
    button.disabled = !enabled
    button.style.opacity = enabled ? "" : "0.4"
    button.title = enabled ? "Send" : "Sends when the file finishes uploading"
  }

  get uploading() {
    return this.listTarget.querySelector(".laz-composer-upload.is-uploading")
  }

  pick() {
    this.inputTarget.click()
  }

  changed() {
    this.add(Array.from(this.inputTarget.files))
    this.inputTarget.value = ""
    this.focusComposer()
  }

  // A screenshot pasted into the composer goes the way a picked file does — png / jpeg only, the
  // images User::Chat::Upload takes. A clipboard that also carries text (cells copied from Excel
  // bring a picture of themselves) pastes the text, as it always did.
  paste(event) {
    const data = event.clipboardData
    if (!data || !this.hasInputTarget || data.getData("text/plain")) return
    const images = Array.from(data.files).filter((file) => /^image\/(png|jpeg)$/.test(file.type))
    if (!images.length) return
    event.preventDefault()
    // Every pasted image is called image.png; name it so two of them can be told apart.
    const stamp = new Date().toTimeString().slice(0, 8).replaceAll(":", "")
    this.add(images.map((file, i) => new File([file],
      `Pasted image ${stamp}${images.length > 1 ? `-${i + 1}` : ""}.${file.type === "image/png" ? "png" : "jpg"}`, { type: file.type })))
  }

  // Past the limit nothing is uploaded: the extra files show as one failed chip saying why, which
  // the user removes. Uploading them would only read and pay for files the message cannot carry.
  add(files) {
    const room = Math.max(this.maxValue - this.listTarget.querySelectorAll(".laz-composer-upload:not(.is-failed)").length, 0)
    files.slice(0, room).forEach((file) => this.upload(file))
    if (files.length > room) {
      const extra = files.length - room
      this.fail(this.placeholder(extra === 1 ? files[room].name : `${extra} files`), `not attached — up to ${this.maxValue} files per message`)
    }
  }

  discard(event) {
    event.currentTarget.closest(".laz-composer-upload")?.remove()
    this.settle()
    this.focusComposer()
  }

  // The file dialog hands focus back to the paperclip button (and a removed chip takes its button's
  // focus with it), so the caret still showed in the textarea while typed keys went nowhere.
  focusComposer() {
    this.element.querySelector("textarea:not([disabled])")?.focus()
  }

  upload(file) {
    const chip = this.placeholder(file.name)
    const status = chip.querySelector(".laz-msg-file-size")
    const upload = new DirectUpload(file, this.directUploadUrlValue, {
      directUploadWillStoreFileWithXHR(xhr) {
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) status.textContent = `Uploading ${Math.round((e.loaded / e.total) * 100)}%`
        })
      },
    })
    upload.create((error, blob) => {
      if (error) return this.fail(chip, "could not upload")
      fetch(this.uploadsUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "text/html", "X-CSRF-Token": this.csrf },
        body: JSON.stringify({ signed_blob_id: blob.signed_id }),
      })
        .then(async (response) => {
          const body = await response.text()
          if (!response.ok) return this.fail(chip, (response.status === 422 && body) || "could not upload")
          this.swap(chip, body)
        })
        .catch(() => this.fail(chip, "could not upload"))
    })
  }

  swap(chip, html) {
    if (!chip.isConnected) return
    const template = document.createElement("template")
    template.innerHTML = html.trim()
    const fresh = template.content.firstElementChild
    chip.replaceWith(fresh)
    this.settle()
    if (fresh.dataset.pending === "true") setTimeout(() => this.poll(fresh), 2000)
  }

  // ponytail: one GET per pending file every 2s while the composer is open; a read is 10-60s.
  poll(chip) {
    if (!chip.isConnected) return
    fetch(chip.dataset.url, { headers: { Accept: "text/html" } })
      .then((response) => (response.ok ? response.text() : Promise.reject(response)))
      .then((html) => this.swap(chip, html))
      .catch(() => setTimeout(() => this.poll(chip), 2000))
  }

  placeholder(name) {
    const chip = document.createElement("span")
    chip.className = "laz-composer-upload is-uploading"
    chip.innerHTML = `<span class="laz-msg-file"><span class="laz-msg-file-name"></span><span class="laz-msg-file-size">Uploading…</span></span>
      <button type="button" class="laz-chip-remove" title="Remove this file" data-action="click->chat-attach#discard">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
      </button>`
    chip.querySelector(".laz-msg-file-name").textContent = name
    this.listTarget.appendChild(chip)
    return chip
  }

  fail(chip, message) {
    chip.classList.replace("is-uploading", "is-failed")
    chip.querySelector(".laz-msg-file-size").textContent = message
    this.settle(true)
  }

  get csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
